<script lang="ts" setup>
import type { Enum_Componentelementslinkicon_Mobileposition } from '@autobid/strapi-integration/graphql/graphql'
import type { StrapiParsedComponent } from '@autobid/strapi-integration/typescript/strapi/Page'
import type { HeaderProps } from '@autobid/ui/composables/usePageHeader'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { useToken } from '@autobid/nuxt-auth/src/composables/useToken'
import { useHeaderStore } from '@autobid/nuxt-pinia-store/store/useHeaderStore'
import { useUserHasBidBlocked } from '@autobid/ui/composables/useIsUserHasBidBlocked'
import { usePageHeader } from '@autobid/ui/composables/usePageHeader'
import { MenuItem } from '@headlessui/vue'
import { storeToRefs } from 'pinia'
import { computed, inject, onBeforeMount, onMounted, ref } from 'vue'

interface Props {
  data: HeaderProps
}

const props = defineProps<Props>()

const { openReasonOnAuthorize } = usePageHeader()
const { isMobileMenuActive } = storeToRefs(useHeaderStore())
const { closeMobileMenu } = useHeaderStore()
const { isAuthed } = useAutobidAuth()
const { tokenData } = useToken()
const isBidBlocked = useUserHasBidBlocked()
const bidBlockedBtn = ref<HTMLButtonElement>()
const isNavbarTransparentVisible = inject('isNavbarTransparentVisible', false)
const authDisabled = inject('authDisabled', false)

const groupedComponents = computed(() => {
  const result: Partial<Record<Enum_Componentelementslinkicon_Mobileposition, StrapiParsedComponent[]>>
    = {}
  for (const component of props.data.data.layout?.topMenu ?? []) {
    if (!component.props || (!('wrapper_mobilePosition' in component.props) && !('icon_mobilePosition' in component.props))) {
      continue
    }

    const mobilePosition = 'wrapper_mobilePosition' in component.props
      ? component.props.wrapper_mobilePosition as unknown as Enum_Componentelementslinkicon_Mobileposition
      : component.props.icon_mobilePosition as unknown as Enum_Componentelementslinkicon_Mobileposition

    if (!result[mobilePosition]) {
      result[mobilePosition] = []
    }

    result[mobilePosition].push(component)
  }

  const logoutIndex = (result.profile ?? []).findIndex(
    el => el.props && 'wrapper' in el.props && el.props.wrapper === 'Logout',
  )

  if (logoutIndex >= 0) {
    const logoutElement = result.profile?.splice(logoutIndex, 1)[0]
    logoutElement && result.profile?.push(logoutElement)
  }

  return result
})

onMounted(() => {
  if (bidBlockedBtn.value) {
    openReasonOnAuthorize(bidBlockedBtn.value)
  }
  window.addEventListener('resize', closeMobileMenu)
})

onBeforeMount(() => {
  window.removeEventListener('resize', closeMobileMenu)
})
</script>

<template>
  <div class="page-header-mobile flex w-full items-center lg:hidden">
    <div class="burger-menu flex flex-1 items-center gap-x-4 pl-3">
      <button
        class="-mr-1"
        :aria-label="$t('header.open-menu')"
        @click="isMobileMenuActive = !isMobileMenuActive"
      >
        <Icon mode="svg" name="ci:hamburger-md" class="text-5xl" />
      </button>

      <AutobidRenderComponents
        :components="groupedComponents?.left ?? []"
        class="mx-0 flex items-center gap-x-4"
      />
    </div>

    <LayoutHeaderPartialsLogo
      v-if="data.data.layout?.header?.brand"
      :logo="data.data.layout.header.brand"
    />

    <div class="flex flex-1 items-center justify-end gap-x-4 pr-3">
      <AutobidRenderComponents
        :components="groupedComponents?.right ?? []"
        class="mx-0 flex items-center justify-end gap-x-4"
      />

      <div v-if="!authDisabled" class="relative flex">
        <common-dropdown
          x="right"
          menu-items-classes="bg-[var(--top-navigation-background)]"
        >
          <template #button>
            <span
              class="flex items-center"
              :aria-label="$t('header.toggle-profile-menu')"
            >
              <CommonIconUser />
            </span>
          </template>

          <template #items>
            <template v-if="isAuthed && tokenData">
              <MenuItem>
                <span
                  class="block border-b px-2 py-1 text-center font-bold text-[var(--top-navigation-text-color)]"
                >
                  {{ tokenData.nickname }}
                </span>
              </MenuItem>
            </template>

            <MenuItem>
              <AutobidRenderComponents
                class="top-bar-profile-dropdown"
                :class="{
                  'text-[var(--top-navigation-text-color)]':
                    isNavbarTransparentVisible,
                }"
                :components="groupedComponents.profile ?? []"
              />
            </MenuItem>
          </template>
        </common-dropdown>
      </div>
    </div>
  </div>

  <span
    v-if="isAuthed && isBidBlocked && !authDisabled"
    class="fixed left-3 top-[calc(100vh-60px)] z-40 lg:hidden"
  >
    <common-dropdown x="left" y="top">
      <template #button="{ open }">
        <div ref="bidBlockedBtn" :data-open="open">
          <CommonIconBlock
            class="h-12 w-12 text-red"
          />
        </div>
      </template>

      <template #items>
        <LayoutHeaderPartialsBiddingBlockedReason
          :class="{
            'text-[var(--top-navigation-text-color)]':
              isNavbarTransparentVisible,
          }"
        />
      </template>
    </common-dropdown>
  </span>
</template>

<style lang="scss">
.page-header-mobile {
  [role='menu'] {
    a,
    button {
      padding-left: 8px;
      padding-right: 8px;
    }
  }
}
</style>
